import { useEffect, useState } from 'react';
import { analyticsAPI } from '../services/api';
import { Card, StatCard, Spinner } from '../components/UI';
import { IconUsers, IconContent, IconAnnouncements, IconAnalytics, IconPosts, IconBooks, IconVideo, IconAudio } from '../components/Icons';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

const PALETTE = ['#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6','#ec4899','#8b5cf6','#14b8a6'];

function TopTable({ title, rows, categoryKey }) {
  if (!rows.length) return null;
  const max = rows[0]?.view_count || 1;
  return (
    <Card>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600 }}>
        {title}
      </div>
      {rows.map((item, i) => (
        <div key={item.id} style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '11px 20px',
          borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
        }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)', width: 20, flexShrink: 0 }}>
            {String(i + 1).padStart(2, '0')}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.title?.length > 50 ? item.title.slice(0, 50) + '…' : (item.title || '—')}
            </div>
            {item[categoryKey] && (
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{item[categoryKey]}</div>
            )}
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', fontWeight: 500, flexShrink: 0 }}>
            {item.view_count} views
          </div>
          <div style={{ width: 100, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
            <div style={{
              height: '100%', borderRadius: 2, background: 'var(--accent)',
              width: `${Math.round((item.view_count / max) * 100)}%`,
            }}/>
          </div>
        </div>
      ))}
    </Card>
  );
}

export default function Analytics() {
  const [overview, setOverview]     = useState(null);
  const [daily, setDaily]           = useState([]);
  const [topContent, setTopContent] = useState([]);
  const [topPosts, setTopPosts]     = useState([]);
  const [topBooks, setTopBooks]     = useState([]);
  const [topVideos, setTopVideos]   = useState([]);
  const [topAudio, setTopAudio]     = useState([]);
  const [roles, setRoles]           = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    Promise.all([
      analyticsAPI.overview(),
      analyticsAPI.dailyViews(),
      analyticsAPI.topContent(10),
      analyticsAPI.topPosts(8),
      analyticsAPI.topBooks(8),
      analyticsAPI.topVideos(8),
      analyticsAPI.topAudio(8),
      analyticsAPI.roleBreakdown(),
    ]).then(([o, d, t, tp, tb, tv, ta, r]) => {
      setOverview(o.data.data);
      setDaily(d.data.data.map(x => ({ ...x, date: x.date?.slice(5) })));
      setTopContent(t.data.data);
      setTopPosts(tp.data.data);
      setTopBooks(tb.data.data);
      setTopVideos(tv.data.data);
      setTopAudio(ta.data.data);
      setRoles(r.data.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <Spinner size={32}/>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', animation: 'fadeIn .35s ease' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700 }}>Analytics</h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>Engagement insights across the foundation's content and users.</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard icon={<IconUsers size={22}/>}         label="Total users"     value={overview?.users?.total}         sub={`${overview?.users?.active} active`}                 color="var(--accent)"/>
        <StatCard icon={<IconContent size={22}/>}       label="Published files" value={overview?.content?.total}       color="var(--blue)"/>
        <StatCard icon={<IconAnnouncements size={22}/>} label="Announcements"   value={overview?.announcements?.total} color="var(--gold)"/>
        <StatCard icon={<IconAnalytics size={22}/>}     label="Views today"     value={overview?.views?.today}         sub={`${overview?.views?.last_7_days} this week`} color="#7c3aed"/>
        <StatCard icon={<IconPosts size={22}/>}         label="Posts"           value={overview?.posts?.total}         sub={`${overview?.posts?.published} published`}    color="#10b981"/>
        <StatCard icon={<IconBooks size={22}/>}         label="Books"           value={overview?.books?.total}         sub={`${overview?.books?.published} published`}    color="#f59e0b"/>
        <StatCard icon={<IconVideo size={22}/>}         label="Videos"          value={overview?.videos?.total}        sub={`${overview?.videos?.published} published`}   color="#3b82f6"/>
        <StatCard icon={<IconAudio size={22}/>}         label="Audio tracks"    value={overview?.audio?.total}         sub={`${overview?.audio?.published} published`}    color="#8b5cf6"/>
      </div>

      {/* Daily views — now includes post + book views */}
      <Card style={{ padding: '20px 20px 12px', marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Daily views (content + posts + books) — last 30 days</div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={daily}>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false}/>
            <YAxis tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false} width={28}/>
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }}/>
            <Line type="monotone" dataKey="views" stroke="var(--accent)" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: 'var(--accent)' }}/>
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Role breakdown pie */}
        <Card style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Users by role</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={roles} dataKey="user_count" nameKey="role" cx="50%" cy="50%" outerRadius={80} label={({ role, user_count }) => `${role}: ${user_count}`} labelLine={true} fontSize={11}>
                {roles.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }}/>
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* Top content bar */}
        <Card style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Top content files by views</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topContent.slice(0, 6)} layout="vertical" barSize={14}>
              <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false}/>
              <YAxis type="category" dataKey="title" width={100} tick={{ fontSize: 10, fill: 'var(--text2)' }} tickLine={false} axisLine={false}
                tickFormatter={v => v.length > 14 ? v.slice(0, 14) + '…' : v}/>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }}/>
              <Bar dataKey="view_count" radius={[0, 4, 4, 0]}>
                {topContent.slice(0, 6).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Top posts bar */}
        <Card style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Top posts by views</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topPosts.slice(0, 6)} layout="vertical" barSize={14}>
              <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false}/>
              <YAxis type="category" dataKey="title" width={100} tick={{ fontSize: 10, fill: 'var(--text2)' }} tickLine={false} axisLine={false}
                tickFormatter={v => v.length > 14 ? v.slice(0, 14) + '…' : v}/>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }}/>
              <Bar dataKey="view_count" radius={[0, 4, 4, 0]}>
                {topPosts.slice(0, 6).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Top books bar */}
        <Card style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Top books by views</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topBooks.slice(0, 6)} layout="vertical" barSize={14}>
              <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false}/>
              <YAxis type="category" dataKey="title" width={100} tick={{ fontSize: 10, fill: 'var(--text2)' }} tickLine={false} axisLine={false}
                tickFormatter={v => v.length > 14 ? v.slice(0, 14) + '…' : v}/>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }}/>
              <Bar dataKey="view_count" radius={[0, 4, 4, 0]}>
                {topBooks.slice(0, 6).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Videos + Audio bar charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Top videos by views</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topVideos.slice(0, 6)} layout="vertical" barSize={14}>
              <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false}/>
              <YAxis type="category" dataKey="title" width={100} tick={{ fontSize: 10, fill: 'var(--text2)' }} tickLine={false} axisLine={false}
                tickFormatter={v => v?.length > 14 ? v.slice(0, 14) + '…' : v}/>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }}/>
              <Bar dataKey="view_count" radius={[0, 4, 4, 0]}>
                {topVideos.slice(0, 6).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Top audio tracks by plays</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topAudio.slice(0, 6)} layout="vertical" barSize={14}>
              <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false}/>
              <YAxis type="category" dataKey="title" width={100} tick={{ fontSize: 10, fill: 'var(--text2)' }} tickLine={false} axisLine={false}
                tickFormatter={v => v?.length > 14 ? v.slice(0, 14) + '…' : v}/>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }}/>
              <Bar dataKey="view_count" radius={[0, 4, 4, 0]}>
                {topAudio.slice(0, 6).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Detailed tables */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <TopTable title="Top 10 most viewed files"  rows={topContent} categoryKey="category"/>
        <TopTable title="Top 8 most viewed posts"   rows={topPosts}   categoryKey="category"/>
        <TopTable title="Top 8 most viewed books"   rows={topBooks}   categoryKey="published_date"/>
        <TopTable title="Top 8 most viewed videos"  rows={topVideos}  categoryKey={null}/>
        <TopTable title="Top 8 most played audio"   rows={topAudio}   categoryKey="album"/>
      </div>
    </div>
  );
}
